import {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useReducer,
  useTransition,
} from "react";
import { encodeBytesToBase64 } from "./base64ArrayBuffer";
import { useSearchParams } from "react-router";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { debounce, isEqual, merge } from "lodash";
import { DEFAULT_VOTABLE_ORDER, VotableOrder } from "./votableOrder";
import {
  GraphQLTaggedNode,
  Observer,
  OperationType,
  Subscription,
  createOperationDescriptor,
  fetchQuery,
  getRequest,
} from "relay-runtime";
import { RefetchFnDynamic, useRelayEnvironment } from "react-relay";
import { KeyType } from "relay-runtime/store/RelayStoreTypes";
import { toPromise } from "./relay";
import * as v from "valibot";
import { TIemSchema, useValibotSearchParams } from "./validation";

export function useUpload<T>(
  defaultValue: T | undefined,
  value: FileList | null,
  reset: () => void,
) {
  const [shouldDeleteImage, setShouldDeleteImage] = useState(false);
  const hasDefaultValue = !!defaultValue;
  const hasValue = value && value.length > 0;
  useEffect(() => {
    if (hasValue) {
      setShouldDeleteImage(false);
    }
  }, [hasValue]);

  return {
    deleteImage: () => {
      reset();
      if (hasDefaultValue) {
        setShouldDeleteImage(true);
      }
    },
    reset: () => {
      reset();
      setShouldDeleteImage(false);
    },
    canDelete: hasDefaultValue && !shouldDeleteImage,
    isDirty: hasValue || shouldDeleteImage,
    variable: shouldDeleteImage || hasValue ? null : undefined,
    value: hasValue ? value[0] : undefined,
  };
}

export function useImagePreview(value: File | undefined) {
  const [objectURL, setObjectURL] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (value) {
      const objectURL = window.URL.createObjectURL(value);
      setObjectURL(objectURL);
      return () => window.URL.revokeObjectURL(objectURL);
    } else {
      setObjectURL(undefined);
    }
  }, [value]);
  return objectURL;
}

export function useFileReadAsync(image?: File | undefined) {
  const [error, setError] = useState<ProgressEvent<FileReader> | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [dataURL, setDataURL] = useState<string | undefined>(undefined);
  useEffect(() => {
    setError(undefined);
    setIsLoading(false);
    setDataURL(undefined);
    if (!image) {
      return;
    }
    setIsLoading(true);
    const fr = new FileReader();
    fr.onload = () => {
      setIsLoading(false);
      setDataURL(fr.result as string);
    };
    fr.onerror = (error) => {
      setIsLoading(false);
      setError(error);
    };
    fr.readAsDataURL(image);
    return () => fr.abort();
  }, [image]);
  return { dataURL, error, isLoading };
}

export function useClickOutside(
  ref: React.RefObject<HTMLElement>,
  callback: () => void,
) {
  useEffect(() => {
    const listener = (event: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        !event.defaultPrevented
      ) {
        callback();
      }
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [ref, callback]);
}

export type WindowDimensions = {
  width: number;
  height: number;
};

function getWindowDimensions(): WindowDimensions {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export function useWindowDimensions(): WindowDimensions {
  const [windowDimensions, setWindowDimensions] = useState<WindowDimensions>(
    getWindowDimensions(),
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimensions;
}

export function useFetchText(url: string) {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<string | null>(null);
  const [isText, setIsText] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aborted, setAborted] = useState(false);
  useEffect(() => {
    setIsLoading(true);
    setAborted(false);
    const controller = new AbortController();
    fetch(url, { signal: controller.signal })
      .then(async (response) => {
        const decoder = new TextDecoder("utf-8", { fatal: true });
        const bytes = await response.bytes();
        try {
          const text = decoder.decode(bytes);
          setData(text);
        } catch {
          setData(encodeBytesToBase64(bytes));
          setIsText(false);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setError(error);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });
    return () => {
      if (!controller.signal.aborted) {
        setAborted(true);
        controller.abort();
      }
    };
  }, [url]);
  return { isLoading, data, isText, error, aborted };
}

interface FetchBlobState {
  url: string | null;
  isAborted: boolean;
  error: Error | null;
  blob: Blob | null;
  controller: AbortController | null;
}

type FetchBlobClear = { kind: "clear" };
type FetchBlobStart = {
  kind: "start";
  url: string;
  controller: AbortController;
};
type FetchBlobAbort = { kind: "abort" };
type FetchBlobBlob = { kind: "blob"; blob: Blob };
type FetchBlobError = { kind: "error"; error: Error };

type FetchBlobAction =
  | FetchBlobClear
  | FetchBlobStart
  | FetchBlobAbort
  | FetchBlobBlob
  | FetchBlobError;

function blobReducer(
  state: FetchBlobState,
  action: FetchBlobAction,
): FetchBlobState {
  switch (action.kind) {
    case "clear":
      if (state.controller && !state.controller.signal.aborted) {
        state.controller.abort();
      }
      return {
        url: null,
        isAborted: false,
        error: null,
        blob: null,
        controller: null,
      };
    case "start":
      if (state.controller && !state.controller.signal.aborted) {
        state.controller.abort();
      }
      return {
        url: state.url,
        controller: action.controller,
        isAborted: false,
        error: null,
        blob: null,
      };
    case "blob":
      return {
        url: state.url,
        isAborted: false,
        error: null,
        blob: action.blob,
        controller: null,
      };
    case "error":
      if (action.error.name === "AbortError") {
        return {
          url: state.url,
          isAborted: true,
          error: null,
          blob: null,
          controller: null,
        };
      } else {
        return {
          url: state.url,
          isAborted: false,
          error: action.error,
          blob: null,
          controller: null,
        };
      }
    case "abort":
      if (state.controller && !state.controller.signal.aborted) {
        state.controller.abort();
        return {
          url: state.url,
          isAborted: true,
          error: null,
          blob: null,
          controller: null,
        };
      } else {
        return state;
      }
  }
}

interface UseFetchBlob {
  isLoading: boolean;
  isAborted: boolean;
  blob?: Blob;
  error?: Error;
  abort: () => void;
}

export function useFetchBlob(url?: string): UseFetchBlob {
  const [state, dispatch] = useReducer(blobReducer, {
    url: null,
    isAborted: false,
    error: null,
    blob: null,
    controller: null,
  });
  useEffect(() => {
    if (url == state.url) {
      return;
    }
    if (!url) {
      dispatch({ kind: "clear" });
      return;
    }
    const controller = new AbortController();
    dispatch({ kind: "start", url, controller });
    fetch(url, { signal: controller.signal })
      .then(async (response) => {
        dispatch({ kind: "blob", blob: await response.blob() });
      })
      .catch((error) => {
        dispatch({ kind: "error", error });
      });
    return () => {
      dispatch({ kind: "abort" });
    };
  }, [url, state.url, dispatch]);
  return {
    isLoading: !!state.controller,
    isAborted: state.isAborted,
    blob: state.blob || undefined,
    error: state.error || undefined,
    abort: () => dispatch({ kind: "abort" }),
  };
}

interface UseFetchFile {
  isLoading: boolean;
  isAborted: boolean;
  file?: File;
  error?: Error;
  abort: () => void;
}

export function useFetchFile(url?: string): UseFetchFile {
  const { blob, ...rest } = useFetchBlob(url);
  return {
    file:
      blob &&
      new File([blob], url || "", {
        type: blob.type,
      }),
    ...rest,
  };
}

export function useMediaQuery(query: string) {
  const [value, setValue] = useState<boolean>(false);

  useIsomorphicLayoutEffect(() => {
    function onChange(event: MediaQueryListEvent) {
      setValue(event.matches);
    }

    const result = window.matchMedia(query);
    result.addEventListener("change", onChange);
    setValue(result.matches);

    return () => result.removeEventListener("change", onChange);
  }, [query]);

  return value;
}

export const useIsomorphicLayoutEffect = import.meta.env.SSR
  ? useEffect
  : useLayoutEffect;

export const useOnChangeEffect = <T>(value: T, effect: (value: T) => void) => {
  const ref = useRef(value);
  useEffect(() => {
    if (!isEqual(ref.current, value)) {
      effect(value);
    }
    ref.current = value;
  }, [value, effect]);
};

/**
 * Resets a filter form to the URL-derived `values` it submits to. Unlike
 * react-hook-form's `values` option, fields missing from `values` are kept, and
 * so is the search typed so far when the update brings in the search the form
 * last submitted: the URL lags behind the debounced input, so resetting to it
 * would revert any typing done in the meantime. Call the returned function
 * with every search the form submits, clears included.
 */
export function useFilterFormSync<
  T extends FieldValues & { search?: string | null },
  TContext,
  TOutput extends FieldValues,
>(form: UseFormReturn<T, TContext, TOutput>, values: T) {
  const submittedSearch = useRef<string | null>(null);
  useOnChangeEffect(values, (values) => {
    const ownSearch = (values.search?.trim() ?? "") === submittedSearch.current;
    submittedSearch.current = null;
    const current = form.getValues();
    form.reset({
      ...current,
      ...values,
      ...(ownSearch && { search: current.search }),
    });
  });
  return useCallback((search: string | null | undefined) => {
    submittedSearch.current = search?.trim() ?? "";
  }, []);
}

interface DebounceOptions {
  leading?: boolean;
  maxWait?: number;
  trailing?: boolean;
}

export function useDebounced<T>(
  value: T,
  wait: number = 0,
  options: DebounceOptions = {},
) {
  const [state, setState] = useState<T>(value);
  const setDebounced = useRef(debounce(setState, wait, options));
  useEffect(() => {
    if (setDebounced.current) {
      setDebounced.current(value);
    }
  }, [value]);
  useEffect(() => {
    const debounced = setDebounced.current;
    return () => debounced.cancel();
  }, []);
  return state;
}

export function useVotableOrder(
  initial: VotableOrder = DEFAULT_VOTABLE_ORDER,
  queryParamName = "order",
): [VotableOrder, (order: VotableOrder) => void] {
  const [_, setSearchParams] = useSearchParams();
  const [state, setState] = useState(initial);
  const updateState = useCallback(
    (newOrder: VotableOrder) => {
      setState(newOrder);
      setSearchParams(
        { [queryParamName]: newOrder },
        { preventScrollReset: true },
      );
    },
    [setState, setSearchParams, queryParamName],
  );
  return [state, updateState];
}

export function useOnceEffect(callback: () => void | boolean | (() => void)) {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) {
      return;
    }
    const res = callback();
    if (res === false) {
      return;
    }
    done.current = true;
    if (typeof res === "function") {
      return res;
    }
  }, [callback]);
}

export function useCache<K extends string | number | symbol, V>() {
  const [cache, setCache] = useState<Record<K, V>>({} as Record<K, V>);
  return {
    get: (key: K) => cache[key],
    set: (key: K, value: V) => setCache((prev) => ({ ...prev, [key]: value })),
    clear: () => setCache({} as Record<K, V>),
  };
}

export type UseMeasureRect = Pick<
  DOMRectReadOnly,
  "x" | "y" | "top" | "left" | "right" | "bottom" | "height" | "width"
>;
export type UseMeasureRef<E extends Element = Element> = (element: E) => void;
export type UseMeasureResult<E extends Element = Element> = [
  UseMeasureRef<E>,
  UseMeasureRect,
];

const defaultState: UseMeasureRect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

function useMeasure<E extends Element = Element>(): UseMeasureResult<E> {
  const [element, ref] = useState<E | null>(null);
  const [rect, setRect] = useState<UseMeasureRect>(defaultState);

  const observer = useMemo(
    () =>
      new window.ResizeObserver((entries) => {
        if (entries[0]) {
          const { x, y, width, height, top, left, bottom, right } =
            entries[0].contentRect;
          setRect({ x, y, width, height, top, left, bottom, right });
        }
      }),
    [],
  );

  useIsomorphicLayoutEffect(() => {
    if (!element) return;
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [element]);

  return [ref, rect];
}

const isomorphicUseMeasure =
  import.meta.env.SSR || typeof window.ResizeObserver === "undefined"
    ? ((() => [() => {}, defaultState]) as typeof useMeasure)
    : useMeasure;

type UseFetchQuery<O extends OperationType> = [boolean, FetchQueryFn<O>];
type FetchQueryFn<O extends OperationType> = (
  variables: O["variables"],
) => Promise<O["response"]>;

export const useFetchQuery = <O extends OperationType>(
  query: GraphQLTaggedNode,
): UseFetchQuery<O> => {
  const relay = useRelayEnvironment();
  const [isFetching, setFetching] = useState(false);
  const abort = useMemo(() => new AbortController(), [query]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => abort.abort();
  }, [abort]);

  const fetch = useCallback(
    async (variables: O["variables"]): Promise<O["response"]> => {
      setFetching(true);
      try {
        return await toPromise(
          fetchQuery(relay, query, variables),
          abort.signal,
        );
      } finally {
        setFetching(false);
      }
    },
    [relay, query, abort],
  );

  return [isFetching, fetch] as const;
};

export { isomorphicUseMeasure as useMeasure };

export interface DelayingContext<T = unknown> {
  setTimeout(f: () => void, millis: number): T;
  clearTimeout(timeout: T): void;
}

export function useDelayed<A extends unknown[], R, T = unknown>(
  millis: number,
  f: (...argv: A) => R,
  {
    setTimeout,
    clearTimeout,
  }: DelayingContext<T> = globalThis as unknown as DelayingContext<T>,
): (...argv: A) => Promise<R> {
  const timeout = useRef<T | null>(null);

  useEffect(
    () => () => {
      if (timeout.current !== null) {
        clearTimeout(timeout.current);
      }
    },
    [clearTimeout],
  );

  return (...argv) =>
    new Promise((resolve, reject) => {
      if (timeout.current !== null) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => {
        try {
          resolve(f(...argv));
        } catch (error) {
          reject(error);
        } finally {
          timeout.current = null;
        }
      }, millis);
    });
}

interface UseRefetchOnParamsChangeProps<
  TSchema extends TIemSchema,
  TDefaults extends v.InferDefaults<TSchema>,
  TQuery extends OperationType,
  TKey extends KeyType | null | undefined,
> {
  schema: TSchema;
  defaultValues: TDefaults;
  refetch: RefetchFnDynamic<TQuery, TKey>;
  map?: (variables: v.InferOutput<TSchema>) => TQuery["variables"];
}

export function useRefetchOnParamsChange<
  TSchema extends TIemSchema,
  TDefaults extends v.InferDefaults<TSchema>,
  TQuery extends OperationType,
  TKey extends KeyType | null | undefined,
>(props: UseRefetchOnParamsChangeProps<TSchema, TDefaults, TQuery, TKey>) {
  const [_, startTransition] = useTransition();
  const [searchParams, setSearchParams] = useValibotSearchParams(props.schema);

  const paramsHydrated =
    !!searchParams && !searchParams.loading && !!searchParams.success;
  const hasFilters =
    paramsHydrated &&
    !isEqual(
      merge({}, props.defaultValues, searchParams.output),
      props.defaultValues,
    );

  useOnChangeEffect(searchParams, (next) => {
    if (!next || next.loading) return;
    const filters = (next.success ? next.output : undefined) || {};

    if (
      isEqual(merge({}, props.defaultValues, filters), props.defaultValues) &&
      !hasFilters
    )
      return;
    startTransition(() => {
      const variables: TQuery["variables"] = filters;
      props.refetch(props.map ? props.map(filters) : variables, {
        fetchPolicy: "store-or-network",
      });
    });
  });

  return [hasFilters, setSearchParams] as const;
}

export function useQueryResult<O extends OperationType>(
  query: GraphQLTaggedNode,
  debounceBy: number,
) {
  type State = { isLoading: boolean; data: O["response"] | undefined };
  type Action = O["response"] | "start";
  const [state, dispatch] = useReducer(
    (state: State, action: Action) => {
      if (action === "start") {
        return { ...state, isLoading: true };
      } else {
        return { isLoading: false, data: action };
      }
    },
    { isLoading: false, data: undefined },
  );

  const relay = useRelayEnvironment();
  const abort = useMemo(() => new AbortController(), []);

  // memo this debounced function because it is holding state
  const startLoading0 = useMemo(
    () =>
      debounce(
        (
          variables: O["variables"],
          ok: (data: O["response"]) => void,
          err: (reason: unknown) => void,
        ) => {
          const pending = fetchQuery(relay, query, variables, {
            fetchPolicy: "network-only",
          });
          pending.subscribe(new QueryResultObserver(abort.signal, ok, err));
        },
        debounceBy,
      ),
    [abort, debounceBy, query, relay],
  );

  // debounce cannot merge promises so we need to do the heavy lifting ourselves
  const startLoading = useCallback(
    (variables: O["variables"]) =>
      new Promise((resolve, reject) =>
        startLoading0(variables, resolve, reject),
      ),
    [startLoading0],
  );

  // abort pending requests
  useEffect(
    () => () => {
      startLoading0.cancel();
      abort.abort();
    },
    [abort, startLoading0],
  );

  // send a request or complete it right now if cache is fresh
  const loadQuery = useCallback(
    async (variables: O["variables"]) => {
      const operation = createOperationDescriptor(getRequest(query), variables);
      const avail = relay.check(operation);
      if (avail.status === "available") {
        startLoading0.cancel();
        const snapshot = relay.getStore().lookup(operation.fragment);
        dispatch(snapshot.data as O["response"]);
      } else {
        dispatch("start");
        dispatch(await startLoading(variables));
      }
    },
    [query, relay, startLoading, startLoading0],
  );

  return [state, loadQuery] as const;
}

class QueryResultObserver<T> implements Observer<T> {
  readonly #abort: AbortSignal;
  #subscription: Subscription | undefined;
  readonly next: (value: T) => void;
  readonly error: (error: Error) => void;

  constructor(
    abort: AbortSignal,
    next: (value: T) => void,
    error: (error: Error) => void,
  ) {
    this.#abort = abort;
    this.next = next;
    this.error = error;
  }

  start(subscription: Subscription) {
    this.#subscription = subscription;
    this.#abort.addEventListener("abort", this.#onAbort);
  }

  complete() {
    this.#abort.removeEventListener("abort", this.#onAbort);
  }

  #onAbort() {
    this.#subscription?.unsubscribe();
    this.#subscription = undefined;
  }
}

export function useClipboardAvailable(): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    setAvailable(!!navigator.clipboard);
  }, []);
  return available;
}

// Longest delay `setTimeout` supports (~24.8 days).
const MAX_TIMEOUT = 2 ** 31 - 1;

/// Whether `date` (an ISO string) has passed, re-rendering once it does.
export function useIsPast(date?: string | null): boolean {
  const time = date ? Date.parse(date) : undefined;
  const [now, setNow] = useState(() => Date.now());
  const isPast = time !== undefined && time <= now;

  useEffect(() => {
    if (time === undefined || isPast) {
      return;
    }
    // Dates beyond the longest delay wake up early; updating `now` then
    // schedules the rest.
    const delay = Math.min(Math.max(time - Date.now(), 0), MAX_TIMEOUT);
    const timeout = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(timeout);
  }, [time, now, isPast]);

  return isPast;
}
